
import { createAsyncThunk } from '@reduxjs/toolkit';
import { rowToListingPayload } from '../../components/BulkUploadListingsModal/BulkUploadListingsModal.helpers';
import { buildListingTitle } from '../../util/fieldHelpers';
import { hasPermissionToBulkUploadListings } from '../../util/userHelpers';

export const bulkUploadInitialState = {
  bulkUploadModalOpen: false,
  bulkUploadInProgress: false,
  // Array of { title, status, listingId?, error? }  |  null when idle
  bulkUploadResults: null,
};

// ── Async thunk ──────────────────────────────────────────────────────────────

const DELAY_MS = 300;
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Sequentially create listings via the Sharetribe Marketplace API.
 * Uses sdk.ownListings.create — listings are owned by the logged-in user.
 * Docs: https://www.sharetribe.com/api-reference/marketplace.html#create-listing
 *
 * The thunk dispatches incremental progress actions so the modal can show
 * a live progress bar. On completion it returns the full results array.
 *
 * @param {Object[]} rows  Validated parsed CSV rows
 */
export const bulkUploadListings = createAsyncThunk(
  'app/ManageListingsPage/bulkUploadListings',
  async ({ rows, onComplete }, { extra: sdk, dispatch, getState, rejectWithValue }) => {
    const currentUser = getState().user.currentUser;
    if (!hasPermissionToBulkUploadListings(currentUser)) {
      return rejectWithValue('No permission to bulk upload listings');
    }

    const results = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const title = buildListingTitle(row);
      const payload = rowToListingPayload(row);

      try {
        // Marketplace API call
        const response = await sdk.ownListings.create(payload, { expand: true });
        const listingId = response.data.data.id.uuid;
        const result = { title, status: 'success', listingId };
        results.push(result);
        // Dispatch incremental progress so the modal updates in real time
        dispatch(bulkUploadListingProgress(result));
      } catch (err) {
        const errorMessage =
          err?.data?.errors?.[0]?.title ||
          err?.response?.data?.errors?.[0]?.title ||
          err?.message ||
          'Unknown error';
        const result = { title, status: 'error', error: errorMessage };
        results.push(result);
        dispatch(bulkUploadListingProgress(result));
      }

      // Throttle sequential requests to respect Sharetribe rate limits
      if (i < rows.length - 1) {
        await delay(DELAY_MS);
      }
    }

    // Refetch listings so the page reflects newly created drafts
    if (typeof onComplete === 'function') {
      onComplete();
    }

    return results;
  }
);


export const bulkUploadActions = {
  openBulkUploadModal: state => {
    state.bulkUploadModalOpen = true;
    state.bulkUploadResults = null;
  },
  closeBulkUploadModal: state => {
    state.bulkUploadModalOpen = false;
  },
  resetBulkUpload: state => {
    state.bulkUploadInProgress = false;
    state.bulkUploadResults = null;
  },
  bulkUploadListingProgress: (state, action) => {
    if (!state.bulkUploadResults) state.bulkUploadResults = [];
    state.bulkUploadResults.push(action.payload);
  },
};

const bulkUploadListingProgress = payload => ({
  type: 'ManageListingsPage/bulkUploadListingProgress',
  payload,
});

export const bulkUploadExtraReducers = builder => {
  builder
    .addCase(bulkUploadListings.pending, state => {
      state.bulkUploadInProgress = true;
      state.bulkUploadResults = [];
    })
    .addCase(bulkUploadListings.fulfilled, (state, action) => {
      state.bulkUploadInProgress = false;
      state.bulkUploadResults = action.payload; // full results array
    })
    .addCase(bulkUploadListings.rejected, (state, action) => {
      // eslint-disable-next-line no-console
      console.error(action.payload || action.error);
      state.bulkUploadInProgress = false;
    });
};

// ── Selectors ────────────────────────────────────────────────────────────────

export const selectBulkUploadModalOpen  = state => state.ManageListingsPage.bulkUploadModalOpen;
export const selectBulkUploadInProgress = state => state.ManageListingsPage.bulkUploadInProgress;
export const selectBulkUploadResults    = state => state.ManageListingsPage.bulkUploadResults;
